"use client";

import * as React from "react";
import * as RechartsPrimitive from "recharts";

import { cn } from "../../lib/utils";

const THEMES = { light: "", dark: ".dark" } as const;

/**
 * Configuration map for chart series styling and labeling.
 *
 * Each key represents a data series identifier used to derive CSS custom properties
 * (`--color-<key>`). Values describe how the series appears in tooltips, legends,
 * and color theming.
 *
 * @property label - Human-readable name shown in tooltips and legends.
 * @property icon  - Optional icon component rendered alongside the label.
 * @property color - Static CSS color value (mutually exclusive with `theme`).
 * @property theme - Per-theme color map keyed by `light` / `dark` (mutually exclusive with `color`).
 */
export type ChartConfig = {
	[k in string]: {
		label?: React.ReactNode;
		icon?: React.ComponentType;
	} & (
		| { color?: string; theme?: never }
		| { color?: never; theme: Record<keyof typeof THEMES, string> }
	);
};

type ChartContextProps = {
	config: ChartConfig;
};

const ChartContext = React.createContext<ChartContextProps | null>(null);

function useChart() {
	const context = React.useContext(ChartContext);

	if (!context) {
		throw new Error("useChart must be used within a <ChartContainer />");
	}

	return context;
}

/**
 * Wrapper that provides chart theming context and injects per-series CSS custom
 * properties derived from {@link ChartConfig}.  Renders a Recharts
 * `ResponsiveContainer` internally.
 */
function ChartContainer({
	id,
	className,
	children,
	config,
	ref,
	...props
}: React.ComponentProps<"div"> & {
	config: ChartConfig;
	children: React.ComponentProps<
		typeof RechartsPrimitive.ResponsiveContainer
	>["children"];
	ref?: React.Ref<HTMLDivElement>;
}) {
	const uniqueId = React.useId();
	const chartId = `chart-${id || uniqueId.replace(/:/g, "")}`;
	const contextValue = React.useMemo(() => ({ config }), [config]);

	return (
		<ChartContext.Provider value={contextValue}>
			<div
				data-chart={chartId}
				ref={ref}
				className={cn(
					"flex aspect-video justify-center text-xs [&_.recharts-cartesian-axis-tick_text]:fill-neutral-600 [&_.recharts-cartesian-grid_line[stroke='#ccc']]:stroke-neutral-300/50 [&_.recharts-curve.recharts-tooltip-cursor]:stroke-neutral-300 [&_.recharts-dot[stroke='#fff']]:stroke-transparent [&_.recharts-layer]:outline-none [&_.recharts-polar-grid_[stroke='#ccc']]:stroke-neutral-300 [&_.recharts-radial-bar-background-sector]:fill-neutral-200 [&_.recharts-rectangle.recharts-tooltip-cursor]:fill-neutral-200 [&_.recharts-reference-line_[stroke='#ccc']]:stroke-neutral-300 [&_.recharts-sector[stroke='#fff']]:stroke-transparent [&_.recharts-sector]:outline-none [&_.recharts-surface]:outline-none",
					className,
				)}
				{...props}
			>
				<ChartStyle id={chartId} config={config} />
				<RechartsPrimitive.ResponsiveContainer>
					{children}
				</RechartsPrimitive.ResponsiveContainer>
			</div>
		</ChartContext.Provider>
	);
}

function escapeCssSelector(value: string): string {
	return value.replace(/["\\]/g, "\\$&");
}

function sanitizeCssPropertyName(name: string): string {
	return name.replace(/[^a-zA-Z0-9_-]/g, "");
}

function sanitizeCssValue(value: string): string {
	return value.replace(/[;{}]/g, "");
}

/**
 * Resolves a {@link ChartConfig} key to the themed colour reference to pass to a
 * Recharts `fill`/`stroke`. Must stay next to {@link ChartStyle}, which declares
 * these custom properties: both sides run the key through the same sanitizer, so
 * keys taken from data (fund names, categories) resolve even when they contain
 * spaces or punctuation.
 */
function seriesColor(key: string): string {
	return `var(--color-${sanitizeCssPropertyName(key)})`;
}

const ChartStyle = ({ id, config }: { id: string; config: ChartConfig }) => {
	const colorConfig = Object.entries(config).filter(
		([, c]) => c.theme || c.color,
	);

	if (!colorConfig.length) {
		return null;
	}

	return (
		<style
			data-testid="chart-style"
			dangerouslySetInnerHTML={{
				__html: Object.entries(THEMES)
					.map(
						([theme, prefix]) => `
${prefix} [data-chart="${escapeCssSelector(id)}"] {
${colorConfig
	.map(([key, itemConfig]) => {
		const color =
			itemConfig.theme?.[theme as keyof typeof itemConfig.theme] ||
			itemConfig.color;
		return color
			? `  --color-${sanitizeCssPropertyName(key)}: ${sanitizeCssValue(color)};`
			: null;
	})
	.join("\n")}
}
`,
					)
					.join("\n"),
			}}
		/>
	);
};

/** Re-export of Recharts `Tooltip` for convenience. */
const ChartTooltip = RechartsPrimitive.Tooltip;

/**
 * Themed tooltip body used with {@link ChartTooltip}.
 * Resolves series labels, colors, and optional value formatting from
 * the nearest {@link ChartContainer} context.
 */
function ChartTooltipContent<
	TValue extends number | string | (number | string)[],
	TName extends number | string,
>({
	active,
	payload,
	className,
	indicator = "dot",
	hideLabel = false,
	hideIndicator = false,
	label,
	labelFormatter,
	labelClassName,
	valueFormatter,
	formatter,
	color,
	nameKey,
	labelKey,
	ref,
}: React.ComponentProps<typeof RechartsPrimitive.Tooltip<TValue, TName>> &
	React.ComponentProps<"div"> & {
		hideLabel?: boolean;
		hideIndicator?: boolean;
		indicator?: "line" | "dot" | "dashed";
		nameKey?: string;
		labelKey?: string;
		valueFormatter?: (
			value: TValue,
			payload?: Record<string, unknown>,
			name?: TName,
		) => React.ReactNode;
		ref?: React.Ref<HTMLDivElement>;
	}) {
	const { config } = useChart();

	const tooltipLabel = React.useMemo(() => {
		if (hideLabel || !payload?.length) {
			return null;
		}

		const [item] = payload;
		const key = `${labelKey || item?.dataKey || item?.name || "value"}`;
		const itemConfig = getPayloadConfigFromPayload(config, item, key);
		const value =
			!labelKey && typeof label === "string"
				? config[label as keyof typeof config]?.label || label
				: itemConfig?.label;

		if (labelFormatter) {
			return (
				<div className={cn("font-medium", labelClassName)}>
					{labelFormatter(value, payload)}
				</div>
			);
		}

		if (!value) {
			return null;
		}

		return <div className={cn("font-medium", labelClassName)}>{value}</div>;
	}, [
		label,
		labelFormatter,
		payload,
		hideLabel,
		labelClassName,
		config,
		labelKey,
	]);

	if (!active || !payload?.length) {
		return null;
	}

	const nestLabel = payload.length === 1 && indicator !== "dot";

	return (
		<div
			ref={ref}
			className={cn(
				"grid min-w-[8rem] items-start gap-1.5 rounded-lg border border-neutral-200/50 bg-background px-2.5 py-1.5 text-xs shadow-xl dark:border-neutral-800/50 dark:bg-neutral-800",
				className,
			)}
		>
			{!nestLabel ? tooltipLabel : null}
			<div className="grid gap-1.5">
				{payload.map((item, index) => {
					const key = `${nameKey || item.name || item.dataKey || "value"}`;
					const itemConfig = getPayloadConfigFromPayload(config, item, key);
					const indicatorColor = color || item.payload.fill || item.color;

					return (
						<div
							key={item.dataKey}
							className={cn(
								"flex w-full flex-wrap items-stretch gap-2 [&>svg]:h-2.5 [&>svg]:w-2.5 [&>svg]:text-neutral-500 dark:[&>svg]:text-neutral-400",
								indicator === "dot" && "items-center",
							)}
						>
							{formatter && item?.value !== undefined && item.name ? (
								formatter(item.value, item.name, item, index, item.payload)
							) : (
								<>
									{itemConfig?.icon ? (
										<itemConfig.icon />
									) : (
										!hideIndicator && (
											<div
												className={cn(
													"shrink-0 rounded-[2px] border-(--color-border) bg-(--color-bg)",
													{
														"h-2.5 w-2.5": indicator === "dot",
														"w-1": indicator === "line",
														"w-0 border-[1.5px] border-dashed bg-transparent":
															indicator === "dashed",
														"my-0.5": nestLabel && indicator === "dashed",
													},
												)}
												style={
													{
														"--color-bg": indicatorColor,
														"--color-border": indicatorColor,
													} as React.CSSProperties
												}
											/>
										)
									)}
									<div
										className={cn(
											"flex flex-1 gap-2 justify-between leading-none",
											nestLabel ? "items-end" : "items-center",
										)}
									>
										<div className="grid gap-1.5">
											{nestLabel ? tooltipLabel : null}
											<span className="text-neutral-500 dark:text-neutral-400">
												{itemConfig?.label || item.name}
											</span>
										</div>
										{typeof item.value !== "undefined" &&
											item.value !== null && (
												<span className="font-mono font-medium tabular-nums text-neutral-800 dark:text-neutral-50">
													{valueFormatter
														? valueFormatter(
																item.value,
																item.payload as Record<string, unknown>,
																item.name,
															)
														: item.value.toLocaleString()}
												</span>
											)}
									</div>
								</>
							)}
						</div>
					);
				})}
			</div>
		</div>
	);
}
ChartTooltipContent.displayName = "ChartTooltip";

/** Re-export of Recharts `Legend` for convenience. */
const ChartLegend = RechartsPrimitive.Legend;

/**
 * Themed legend body used with {@link ChartLegend}.
 * Renders color swatches and labels resolved from the nearest
 * {@link ChartContainer} context.
 */
function ChartLegendContent({
	className,
	hideIcon = false,
	payload,
	verticalAlign = "bottom",
	layout = "horizontal",
	nameKey,
	ref,
}: React.ComponentProps<"div"> &
	Pick<
		RechartsPrimitive.LegendProps,
		"payload" | "verticalAlign" | "layout"
	> & {
		hideIcon?: boolean;
		nameKey?: string;
		ref?: React.Ref<HTMLDivElement>;
	}) {
	const { config } = useChart();

	if (!payload?.length) {
		return null;
	}

	return (
		<div
			ref={ref}
			className={cn(
				"flex gap-4",
				verticalAlign === "top" ? "pb-3" : "pt-3",
				layout === "horizontal" && "flex-row items-center justify-center",
				layout === "vertical" && "flex-col",
				className,
			)}
		>
			{payload.map((item) => {
				const key = `${nameKey || item.dataKey || "value"}`;
				const itemConfig = getPayloadConfigFromPayload(config, item, key);

				return (
					<div
						key={item.value}
						className={cn(
							"flex items-center gap-1.5 [&>svg]:h-3 [&>svg]:w-3 [&>svg]:text-neutral-600 dark:[&>svg]:text-neutral-400 font-sans text-sm text-neutral-600 dark:text-neutral-400",
						)}
					>
						{itemConfig?.icon && !hideIcon ? (
							<itemConfig.icon />
						) : (
							<div
								className="h-2 w-2 shrink-0 rounded-[2px]"
								style={{
									backgroundColor: item.color,
								}}
							/>
						)}
						{itemConfig?.label ?? key}
					</div>
				);
			})}
		</div>
	);
}
ChartLegendContent.displayName = "ChartLegend";

function getPayloadConfigFromPayload(
	config: ChartConfig,
	payload: unknown,
	key: string,
) {
	if (typeof payload !== "object" || payload === null) {
		return undefined;
	}

	const payloadPayload =
		"payload" in payload &&
		typeof payload.payload === "object" &&
		payload.payload !== null
			? payload.payload
			: undefined;

	let configLabelKey: string = key;

	if (
		key in payload &&
		typeof payload[key as keyof typeof payload] === "string"
	) {
		configLabelKey = payload[key as keyof typeof payload] as string;
	} else if (
		payloadPayload &&
		key in payloadPayload &&
		typeof payloadPayload[key as keyof typeof payloadPayload] === "string"
	) {
		configLabelKey = payloadPayload[
			key as keyof typeof payloadPayload
		] as string;
	}

	return configLabelKey in config
		? config[configLabelKey]
		: config[key as keyof typeof config];
}

export {
	ChartContainer,
	ChartTooltip,
	ChartTooltipContent,
	ChartLegend,
	ChartLegendContent,
	ChartStyle,
	seriesColor,
};
